import React, { useEffect, useRef, useState } from 'react';
import { Pencil, ShoppingCart, Edit, Trash, ExternalLink, CreditCard, Info, LogOut, User, ShoppingBag, MessageCircleHeart } from 'lucide-react';
import { Tooltip, IconButton, Zoom, ClickAwayListener } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import dayjs from "dayjs";
import '../CSS/UserProfilePage.css';
import { deleteCardApi } from '../api/cardApi';
import { useAuth } from '../Context/AuthContext';
import PaymentPopup from '../Components/PaymentPopup';
import ProfileRSVPStatus from '../Components/UserProfile/ProfileRSVPStatus';
import GoogleIcon from '../Assets/Icon/GoogleIcon';
import ProfileAvatar from '../Components/ProfileAvatar';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
const PASSWORD_PLACEHOLDER = '**********';

const PROFILE_FIELDS = [
  { id: 'profile-name', label: 'Name', field: 'name', type: 'text', placeholder: 'Nama penuh' },
  { id: 'profile-phone', label: 'Phone Number', field: 'phone', type: 'tel', placeholder: 'Contoh: 0123456789' },
  { id: 'profile-old-password', label: 'Old Password', field: 'oldPassword', type: 'password', placeholder: PASSWORD_PLACEHOLDER },
  { id: 'profile-new-password', label: 'New Password', field: 'newPassword', type: 'password', placeholder: PASSWORD_PLACEHOLDER },
];

const getDraftCards = (cards = []) =>
  cards.filter((card) => card.status === 'DRAFT');

const getOrderCards = (cards = []) =>
  cards.filter((card) => card.status === 'PENDING PAYMENT' || card.status === 'ACTIVE');



const getProfileName = (user) => user?.name || user?.fullName || user?.username || '';
const getProfilePhone = (user) => user?.phone || user?.phoneNumber || '';
const getProfileAvatar = (user) => user?.avatarUrl || user?.profilePicture || null;

const formatCardDate = (createdAt) => {
  const dateObj = new Date(createdAt);

  if (Number.isNaN(dateObj.getTime())) {
    return createdAt || '-';
  }

  return dateObj.toLocaleDateString('ms-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getCardTitle = (card) => {
  if (card.groomName && card.brideName) {
    return `${card.groomName.split(' ')[0]} & ${card.brideName.split(' ')[0]}`;
  }

  return card.greetingName || 'Draf Kad Kahwin';
};

const getCardPreviewSrc = (designCode) => `/card-backgrounds/${designCode}.png`;

const buildWhatsAppMessage = (card) => {
  const eventTitle = card.eventType || 'Majlis Perkahwinan';
  const coupleNames = card.groomName + ' & ' + card.brideName || 'Draf Kad Kahwin';
  const dateText = card.eventDate ? `📅 Tarikh: ${dayjs(card.eventDate).format("D MMM YYYY")}${card.day ? ` (${card.day})` : ''}` : '';
  const timeText = (card.startTime || card.endTime) ? `⏰ Masa: ${card.startTime || ''}${card.endTime ? ` - ${card.endTime}` : ''}` : '';
  const placeText = card.eventAddress || card.place ? `📍 Tempat: ${card.eventAddress || card.place}` : '';
  const inviteUrl = `${window.location.origin}/share/${card.uniqueCode}`;

  return `🌸 *Jemputan ${eventTitle}*

Assalamualaikum W.B.T & Salam Sejahtera,

Dengan penuh kesyukuran ke hadrat Ilahi, kami menjemput Tuan / Puan / Encik / Cik untuk bersama-sama menghadiri dan menyerikan lagi majlis kami:

💍 *${coupleNames}*

${dateText}
${timeText}
${placeText}

Sila layari pautan kad jemputan digital kami di bawah untuk maklumat lanjut:

🔗 ${inviteUrl}

Semoga dengan kehadiran dan doa restu para tetamu sekalian akan menyerikan lagi majlis ini serta mendapat keberkatan-Nya. Terima kasih. 🙏`;
};

/* OLD PROFILE FIELD - COMMENTED OUT FOR FUTURE RESTORATION
const ProfileField = ({ field, disabled, value, onChange }) => (
  <div className="profile-field">
    <label className="profile-field-label" htmlFor={field.id}>
      {field.label}
    </label>
    <input
      id={field.id}
      className="profile-input"
      type={field.type}
      placeholder={field.placeholder}
      disabled={disabled}
      value={value}
      onChange={onChange(field.field)}
    />
  </div>
);
*/

const EmptyState = ({ icon: Icon, message, compact = false }) => (
  <div className={`profile-empty-tab${compact ? ' profile-empty-tab-compact' : ''}`}>
    <Icon size={52} />
    <p>{message}</p>
  </div>
);

const ProfileTab = () => {
  const { user, refreshUserProfile, logout } = useAuth();
  const navigate = useNavigate();
  const name = getProfileName(user);
  const email = user?.email || user?.user_metadata?.email || '';

  // TODO: re-enable avatar upload, phone, password fields and edit form when ready
  /*
  const [form, setForm] = useState({ name: '', phone: '', oldPassword: '', newPassword: '' });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    setForm((prev) => ({ ...prev, name: getProfileName(user), phone: getProfilePhone(user) }));
    setAvatarUrl(getProfileAvatar(user));
  }, [user]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAvatarUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setStatus(null);

    await new Promise((resolve) => setTimeout(resolve, 900));

    setLoading(false);
    setStatus({ type: 'success', msg: 'Profil berjaya dikemaskini.' });
  };
  */

  useEffect(() => {
    let isMounted = true;
    const loadProfile = async () => {
      try {
        await refreshUserProfile();
      } catch (error) {
        console.error('Failed to load profile:', error);
      }
    };
    loadProfile();
    return () => { isMounted = false; };
  }, [refreshUserProfile]);

  return (
    <section className="profile-section">
      {/* Avatar upload — commented out for now
      <div className="profile-avatar-section">
        <button type="button" className="profile-avatar-button" onClick={() => fileInputRef.current?.click()} title="Tukar gambar profil">
          {avatarUrl ? (
            <img className="profile-avatar-img" src={avatarUrl} alt="Profil" />
          ) : (
            <div className="profile-avatar-placeholder">U</div>
          )}
          <span className="profile-avatar-edit-btn">
            <Pencil size={11} color="white" strokeWidth={2.5} />
          </span>
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleAvatarChange} />
      </div>
      */}

      {/* Edit form — commented out for now
      {status && <div className={`profile-status ${status.type}`}>{status.msg}</div>}
      <form className="profile-form" onSubmit={handleSubmit} autoComplete="off">
        {PROFILE_FIELDS.map((field) => (
          <ProfileField key={field.id} field={field} value={form[field.field]} onChange={handleChange} />
        ))}
        <button id="profile-submit-btn" type="submit" className="profile-submit-btn" disabled={loading}>
          {loading ? 'Menyimpan...' : 'Edit Profile'}
        </button>
      </form>
      */}

      {/* NEW TEMPORARY PROFILE UI */}
      <div className="profile-modern-card">
        <div className="profile-modern-header">
          <ProfileAvatar name={name} size={55} fontSize={22} />
          <div className="profile-modern-meta-info">
            <h2 className="profile-modern-name">{name || '-'}</h2>
            <div className="profile-modern-auth-badge">
              <GoogleIcon className="google-icon" width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" />
              <span className="google-signin-text">Signed in with Google</span>
            </div>
          </div>
        </div>

        <div className="profile-modern-body">
          <div className="profile-modern-info-row">
            <span className="profile-modern-info-label">Email Address</span>
            <span className="profile-modern-info-value">{email || '-'}</span>
          </div>
        </div>

        <div className="profile-modern-footer">
          <button className="profile-modern-logout-btn" onClick={() => { logout(); navigate('/'); }}>
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </section>
  );
};

const PACKAGE_PRICES = { Basic: 29, Standard: 39, Premium: 49 };

const CartCard = ({ card, onDelete }) => {
  const price = PACKAGE_PRICES[card.packageType] ? `RM${PACKAGE_PRICES[card.packageType]}` : '-';

  return (
    <>
      <div className='profile-orders-container'>
        <article className="profile-card-item">
          <div className="profile-card-preview">
            <img
              alt="Card Preview"
              src={getCardPreviewSrc(card.designCode)}
              onError={(event) => {
                event.target.src = '/card-backgrounds/abstract-001.png';
              }}
            />
          </div>

          <div className="profile-card-details">
            <p className="profile-card-title">{getCardTitle(card)}</p>
            <p className="profile-card-date">Created: {formatCardDate(card.createdAt)}</p>

            <div className="profile-card-meta">
              <p className="profile-card-meta-item">
                <strong>Order No:</strong> <span>{card.uniqueCode || '-'}</span>
              </p>
              <p className="profile-card-meta-item">
                <strong>Design Code:</strong> <span>{card.designCode || '-'}</span>
              </p>
              <p className="profile-card-meta-item">
                <strong>Package:</strong> <span>{card.packageType || '-'}</span>
              </p>
              <p className="profile-card-meta-item">
                <strong>Price:</strong> <span>{price}</span>
              </p>
              <p className="profile-card-meta-item">
                <strong>Status:</strong> <span className="profile-card-status">Draf</span>
              </p>
            </div>
          </div>

          <div className="profile-card-actions-container">
            <div className="profile-card-actions">
              <a href={`/studio?id=${card.uniqueCode}&slug=${card.slug}`} className="profile-card-link">
                <span className="profile-card-link-content">
                  <Edit size={16} />
                  Edit
                </span>
              </a>
            </div>
            <div className="profile-card-actions">
              <a onClick={() => onDelete(card.uniqueCode)} className="profile-card-link-outline" style={{ border: '1px solid #b32e2eff', color: '#b32e2eff', cursor: 'pointer' }}>
                <span className="profile-card-link-content">
                  <Trash color='#b32e2eff' size={16} />
                  Delete
                </span>
              </a>
            </div>
          </div>
        </article>
      </div>
    </>
  );
};

const MyCartTab = () => {
  const { cards, cardsLoading, invalidateCards } = useAuth();
  const draftCards = getDraftCards(cards);

  const handleDeleteCard = async (uniqueCode) => {
    if (!window.confirm('Adakah anda pasti untuk memadam draf ini?')) return;
    try {
      await deleteCardApi(uniqueCode);
      // Invalidate global cache so all tabs see fresh data
      invalidateCards();
    } catch (error) {
      console.error('Failed to delete card:', error);
      alert('Gagal memadam draf. Sila cuba lagi.');
    }
  };

  if (cardsLoading) {
    return <EmptyState icon={ShoppingCart} message="Loading your cart..." />;
  }

  if (draftCards.length === 0) {
    return <EmptyState icon={ShoppingCart} message="Your cart is empty." compact />;
  }

  return (
    <section className="profile-card-list">
      {draftCards.map((card) => (
        <CartCard key={card.id || card.uniqueCode} card={card} onDelete={handleDeleteCard} />
      ))}
    </section>
  );
};



const OrderCard = ({ card }) => {
  const [showPayment, setShowPayment] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const PACKAGE_PRICE = { Basic: 'RM 29.00', Standard: 'RM 39.00', Premium: 'RM 49.00' };
  const price = PACKAGE_PRICE[card.packageType] || 'RM 39.00';

  return (
    <>
      <div className='profile-orders-container' style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', top: '10px', right: '12px' }}>
          <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
            <Tooltip
              title="Invitation card will not be accessible 60 days after the end of your wedding event date."
              placement="top"
              arrow
              open={tooltipOpen}
              onClose={() => setTooltipOpen(false)}
              disableHoverListener
              disableFocusListener
              disableTouchListener
              TransitionComponent={Zoom}
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1a1a1a',
                    color: '#fff',
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: '12px',
                    fontWeight: 500,
                    p: 1.5,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    maxWidth: 250,
                    textAlign: 'center'
                  }
                },
                arrow: {
                  sx: {
                    color: '#1a1a1a'
                  }
                }
              }}
            >
              <IconButton
                size="small"
                onClick={() => setTooltipOpen((prev) => !prev)}
                sx={{
                  color: '#aaa',
                  ...(tooltipOpen ? { color: '#45c0c0', background: 'rgba(69, 192, 192, 0.1)' } : {}),
                }}
              >
                <Info size={18} />
              </IconButton>
            </Tooltip>
          </ClickAwayListener>
        </div>
        <article className="profile-card-item">
          <div className="profile-card-preview">
            <img
              alt="Order Preview"
              src={getCardPreviewSrc(card.designCode)}
              onError={(event) => {
                event.target.src = '/card-backgrounds/abstract-001.png';
              }}
            />
          </div>

          <div className="profile-card-details">
            <p className="profile-card-title">{getCardTitle(card)}</p>
            <p className="profile-card-date">Created: {formatCardDate(card.createdAt)}</p>

            <div className="profile-card-meta">
              <p className="profile-card-meta-item">
                <strong>Order No:</strong> <span>{card.uniqueCode || '-'}</span>
              </p>
              <p className="profile-card-meta-item">
                <strong>Design Code:</strong> <span>{card.designCode || '-'}</span>
              </p>
              <p className="profile-card-meta-item">
                <strong>Package:</strong> <span>{card.packageType || '-'}</span>
              </p>
              <p className="profile-card-meta-item">
                <strong>Price:</strong> <span>{price}</span>
              </p>
              <p className="profile-card-meta-item">
                <strong>Status:</strong> <span className={`profile-card-status ${card.status.toLowerCase().replace(' ', '-')}`}>
                  {card.status}
                </span>
              </p>
            </div>
          </div>

          <div className="profile-card-actions-container">

            {/* Edit & Share — After Payment */}
            {card.status === 'ACTIVE' && card.slug && card.uniqueCode && (
              <div className="profile-card-actions-group">
                <div className="profile-card-actions">
                  <a
                    href={`/studio?id=${card.uniqueCode}&slug=${card.slug}`}
                    className="profile-card-link"
                  >
                    <span className="profile-card-link-content">
                      <Edit size={14} />
                      Edit
                    </span>
                  </a>
                </div>

                <div className="profile-card-actions">
                  <a
                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(buildWhatsAppMessage(card))}`}
                    className="profile-card-link-whatsapp"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="profile-card-link-content">
                      <WhatsAppIcon fontSize="small" />
                      Share
                    </span>
                  </a>
                </div>
              </div>
            )}


            {card.status === 'PENDING PAYMENT' && (
              <div className="profile-card-actions">
                <button
                  onClick={() => setShowPayment(true)}
                  className="profile-card-link"
                  style={{ backgroundColor: '#45c0c0', border: '1px solid #45c0c0', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  <span className="profile-card-link-content">
                    <CreditCard size={14} />
                    Pay Now
                  </span>
                </button>
              </div>
            )}

            {card.slug && card.uniqueCode && (
              <div className="profile-card-actions">
                <a
                  href={`/invitation/${card.slug}/${card.uniqueCode}`}
                  className="profile-card-link-outline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="profile-card-link-content">
                    <ExternalLink size={14} />
                    View Invitation
                  </span>
                </a>
              </div>
            )}

            <div className="profile-card-actions">
              <a
                href={`/dashboard/${card.uniqueCode}?tab=ucapan`}
                className="profile-card-link-outline"
              >
                <span className="profile-card-link-content">
                  <MessageCircleHeart size={14} color="#c62828" />
                  Manage Guest Wish
                </span>
              </a>
            </div>


          </div>
        </article>

        {card.status === 'PENDING PAYMENT' && <p className='edit-noted'>Editing is temporarily disabled and will be available again once payment is completed.</p>}
        {card.status === 'ACTIVE' && card.packageType !== 'Basic' && <ProfileRSVPStatus uniqueCode={card.uniqueCode} totalGuest={card.totalGuest} />}

      </div >


      {showPayment && (
        <PaymentPopup card={card} onClose={() => setShowPayment(false)} />
      )
      }
    </>
  );
};

const MyOrdersTab = () => {
  const { cards, cardsLoading } = useAuth();
  const orderCards = getOrderCards(cards);

  if (cardsLoading) {
    return <EmptyState icon={ShoppingCart} message="Loading your orders..." />;
  }

  if (orderCards.length === 0) {
    return <EmptyState icon={ShoppingCart} message="Your order is empty." compact />;
  }

  return (
    <section className="profile-card-list">
      {orderCards.map((card) => (
        <OrderCard key={card.id || card.uniqueCode} card={card} />
      ))}
    </section>
  );
};
const TABS = [
  { id: 'profile', label: 'Profile', component: ProfileTab },
  { id: 'cart', label: 'My Cart', component: MyCartTab },
  { id: 'orders', label: 'My Orders', component: MyOrdersTab },
];

const UserProfilePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'profile';

  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  return (
    <div className="profile-page">
      <div className="profile-inner">
        <h1 className="profile-title">My Account</h1>

        <div className="profile-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              id={`tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Keep all tabs mounted to preserve state (avoids re-fetching on switch) */}
        {TABS.map((tab) => (
          <div key={tab.id} style={{ display: activeTab === tab.id ? 'block' : 'none' }}>
            <tab.component />
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserProfilePage;
